import { createExcelWithOptions } from '@/utils/fileHelper'
import { numberToColumnKey } from '@/utils/stringHelper'
import {
  COST_BUDGET_HEADER_MAPPING,
  getQuotationDetailTableColumnIds,
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  OfferQuotationTemplateScopeOfWork,
  CostBudgetType,
} from '@eapproval/offer-shared'
import { saveAs } from 'file-saver'

const buildOptionConfigs = (quotationDetailColumns) => {
  const optionConfigs = []

  if (quotationDetailColumns.includes('scopeOfWorkSelect')) {
    optionConfigs.push({
      options: Object.values(OfferQuotationTemplateScopeOfWork),
      columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'scopeOfWorkSelect') + 1),
    })
  }

  if (quotationDetailColumns.includes('unitSelect')) {
    optionConfigs.push({
      options: Object.values(OfferQuotationDetailInstallAndSupplyUnit),
      columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'unitSelect') + 1),
    })
  }

  if (quotationDetailColumns.includes('unitMaintenance')) {
    optionConfigs.push({
      options: Object.values(OfferQuotationDetailMaintenanceUnit),
      columnKey: numberToColumnKey(quotationDetailColumns.findIndex((col) => col === 'unitMaintenance') + 1),
    })
  }

  return optionConfigs
}

const buildHeaderMapping = (quotationDetailColumns, normalizeSelectColumns) => {
  return quotationDetailColumns.reduce((acc, column) => {
    const columnKey = normalizeSelectColumns && column.includes('Select') ? column.replace('Select', '') : column
    acc[columnKey] = COST_BUDGET_HEADER_MAPPING[columnKey] ?? columnKey
    return acc
  }, {})
}

const normalizeRowsForExport = (rows = [], normalizeSelectColumns) => {
  if (!normalizeSelectColumns) {
    return rows
  }

  return rows.map((row) => ({
    ...row,
    scopeOfWork: row?.scopeOfWorkSelect ? row.scopeOfWorkSelect : row.scopeOfWork,
    unit: row?.unitSelect ? row.unitSelect : row.unit,
    unitMaintenance: row?.unitMaintenance ? row.unitMaintenance : row.unitMaintenance,
  }))
}

export const downloadQuotationDetailExcel = async ({
  costBudgetType,
  isTemplate,
  canRemove,
  rows,
  excelAdditionalData = {},
  normalizeSelectColumns = false,
  fileNamePrefix = 'E-Approval Quotation Detail',
}) => {
  const quotationDetailColumns = getQuotationDetailTableColumnIds({
    costBudgetType,
    isTemplate,
    isExcel: true,
    canRemove,
  })

  const headerMapping = buildHeaderMapping(quotationDetailColumns, normalizeSelectColumns)
  const optionConfigs = buildOptionConfigs(quotationDetailColumns)
  const processedRows = normalizeRowsForExport(rows, normalizeSelectColumns)

  const excelBlob = await createExcelWithOptions(headerMapping, processedRows, optionConfigs, excelAdditionalData)
  saveAs(excelBlob, `${fileNamePrefix}-${Date.now()}.xlsx`)
}

export const getZZ6ByCategory = ({ zzz6Response, category }) => {
  if (!category || !Array.isArray(zzz6Response?.data)) {
    return ''
  }

  const matchingZZZ6 = zzz6Response.data.find((zzz6) => {
    const categories = Array.isArray(zzz6?.Category) ? zzz6.Category : []
    return categories.includes(category)
  })

  return matchingZZZ6?.ZZ6 || ''
}

export const getDefaultQuotationCategoryAndSAPMaterialNumber = ({ costBudgetType, zzz6Response }) => {
  let category = ''

  switch (costBudgetType) {
    case CostBudgetType.SUPPLY_ENGINEERING_SERVICE:
      category = 'General Electrical Installation'
      break
    case CostBudgetType.SUPPLY_MAINTENANCE_SERVICE:
      category = 'Maintenance Building'
      break
    default:
      break
  }

  return {
    category,
    SAPMaterialNumber: getZZ6ByCategory({ zzz6Response, category }),
  }
}

export const createQuotationDetailUpdateHandler = ({
  disabled,
  setValue,
  getValues,
  zzz6Response,
  costBudgetType,
  getRowPath = (rowIndex) => `quotationDetails.${rowIndex}`,
  calculateTotalPrice = true,
  onAfterUpdate,
}) => {
  return (rowIndex, columnId, value) => {
    if (disabled) return

    const rowPath = getRowPath(rowIndex)

    setValue(`${rowPath}.${columnId}`, value, { shouldDirty: true })

    const normalizeString = (input) => (typeof input === 'string' ? input.toLowerCase().trim() : '')
    const toArray = (input) => {
      if (Array.isArray(input)) return input
      if (typeof input === 'string' && input.trim()) return [input]
      return []
    }

    let matchingZZZ6

    if (columnId === 'country' || columnId === 'category' || columnId === 'ECCN') {
      const country = columnId === 'country' ? value : getValues(`${rowPath}.country`)
      const category = columnId === 'category' ? value : getValues(`${rowPath}.category`)
      const ECCN = columnId === 'ECCN' ? value : getValues(`${rowPath}.ECCN`)

      if (country && category && ECCN) {
        matchingZZZ6 = (zzz6Response?.data ?? []).find((zzz6) => {
          const countries = toArray(zzz6?.country ?? zzz6?.Country)
          const categories = toArray(zzz6?.Category)
          const zzz6ECCN = normalizeString(zzz6?.ECCN)

          const countryMatches = countries.some(
            (countryValue) => normalizeString(countryValue) === normalizeString(country)
          )
          const categoryMatches = categories.some(
            (categoryValue) => normalizeString(categoryValue) === normalizeString(category)
          )
          const ECCNMatches = zzz6ECCN === normalizeString(ECCN)

          return countryMatches && categoryMatches && ECCNMatches
        })
      }
    }

    if (columnId === 'scopeOfWork' && costBudgetType === CostBudgetType.SUPPLY_SITE_WORK) {
      const nextCategory = value !== OfferQuotationTemplateScopeOfWork.SUPPLY ? 'General Electrical Installation' : ''
      setValue(`${rowPath}.category`, nextCategory, { shouldDirty: true })

      if (nextCategory) {
        matchingZZZ6 = (zzz6Response?.data ?? []).find((zzz6) => {
          const categories = toArray(zzz6?.Category)
          return categories.some(
            (categoryValue) => normalizeString(categoryValue) === normalizeString('General Electrical Installation')
          )
        })
      } else {
        matchingZZZ6 = undefined
      }
    }

    if (columnId === 'category' || columnId === 'country' || columnId === 'ECCN' || columnId === 'scopeOfWork') {
      const SAPMaterialNumber = matchingZZZ6?.ZZ6 ?? ''
      setValue(`${rowPath}.SAPMaterialNumber`, SAPMaterialNumber, { shouldDirty: true })
    }

    if (calculateTotalPrice && (columnId === 'unitRate' || columnId === 'quantity' || columnId === 'discount')) {
      const currentRow = getValues(rowPath)
      const unitRate = columnId === 'unitRate' ? value : currentRow.unitRate
      const quantity = columnId === 'quantity' ? value : currentRow.quantity
      const discount = columnId === 'discount' ? value : currentRow.discount

      const totalPrice = unitRate * quantity * (1 - discount / 100)
      setValue(`${rowPath}.totalPrice`, totalPrice, { shouldDirty: true })
    }

    if (typeof onAfterUpdate === 'function') {
      onAfterUpdate({ rowIndex, columnId, value, rowPath, getValues, setValue })
    }
  }
}
